// src/modules/goldInventory/pages/BatchDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBatchById } from '../api';
import {
  Card, Typography, Spin, Tag, Button, Row, Col,
  Descriptions, Table, message, Space,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;
const STATUS_COLORS = { HOLDING: 'gold', DISPATCHED: 'orange', SETTLED: 'green' };
const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function BatchDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canViewField } = usePermissions();

  const canViewNetWeight = canViewField('goldInventory', 'totalNetWeight');
  const canViewGrossWeight = canViewField('goldInventory', 'totalGrossWeight');
  const canViewPurchaseCost = canViewField('goldInventory', 'totalPurchaseCost');
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    getBatchById(id)
      .then((r) => setBatch(r.data))
      .catch(() => messageApi.error('Failed to load batch'))
      .finally(() => setLoading(false));
  }, [id]);

  const caseColumns = [
    { title: 'Source', dataIndex: 'sourceType', key: 'sourceType', width: 90,
      render: (v) => v || '—' },
    { title: 'Case ID', key: 'caseNumber', dataIndex: 'caseNumber', width: 130,
      render: (v, r) => {
        const targetId = r.caseId?._id || r.caseId;
        const path = r.sourceType === 'PLEDGE' ? `/pledge-release-cases/${targetId}` : `/walk-in-cases/${targetId}`;
        return (
          <Button type="link" size="small" onClick={() => navigate(path)}>
            {v || (r.caseId?.caseId || '—')}
          </Button>
        );
      } },
    { title: 'Customer', key: 'customer', width: 160,
      render: (_, r) => r.caseId?.customerId?.name || '—' },
    canViewNetWeight && { title: 'Net Wt (g)', dataIndex: 'netWeight', key: 'netWeight', render: (v) => v?.toFixed(4) },
    canViewGrossWeight && { title: 'Gross Wt (g)', dataIndex: 'grossWeight', key: 'grossWeight', render: (v) => v?.toFixed(3) },
    canViewPurchaseCost && { title: 'Purchase Cost', dataIndex: 'purchaseCost', key: 'purchaseCost', render: (v) => fmt(v) },
  ].filter(Boolean);

  if (loading) return <Spin style={{ display: 'block', margin: 80, textAlign: 'center' }} />;
  if (!batch) return null;

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      {contextHolder}

      <Row align="middle" style={{ marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/gold-inventory')}>Back</Button>
          <Title level={4} style={{ margin: 0 }}>
            {batch.batchId} &nbsp;<Tag color={STATUS_COLORS[batch.status]}>{batch.status}</Tag>
          </Title>
        </Space>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Batch Summary" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Total Cases">{batch.caseCount}</Descriptions.Item>
              {canViewNetWeight && <Descriptions.Item label="Net Weight">{batch.totalNetWeight?.toFixed(4)}g</Descriptions.Item>}
              {canViewGrossWeight && <Descriptions.Item label="Gross Weight">{batch.totalGrossWeight?.toFixed(3)}g</Descriptions.Item>}
              {canViewPurchaseCost && (
                <Descriptions.Item label="Total Purchase Cost">
                  <Text strong>{fmt(batch.totalPurchaseCost)}</Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Branch">{batch.branch?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Created">{new Date(batch.createdAt).toLocaleString('en-IN')}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {batch.smelterDispatchId && (
          <Col xs={24} md={12}>
            <Card title="Smelter Dispatch" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Dispatch ID">
                  <Button type="link" size="small"
                    onClick={() => navigate(`/smelters/dispatches/${batch.smelterDispatchId._id || batch.smelterDispatchId}`)}>
                    {batch.smelterDispatchId.dispatchId || batch.smelterDispatchId}
                  </Button>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag>{batch.smelterDispatchId.status}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        )}

        <Col xs={24}>
          <Card title={`Cases in Batch (${batch.caseCount})`} size="small">
            <Table
              dataSource={batch.caseRefs || []}
              rowKey={(r) => r.caseId?._id || r.caseNumber}
              columns={caseColumns}
              pagination={false}
              size="small"
              scroll={{ x: 600 }}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}><strong>Totals</strong></Table.Summary.Cell>
                  {canViewNetWeight && <Table.Summary.Cell index={2}><strong>{batch.totalNetWeight?.toFixed(4)}g</strong></Table.Summary.Cell>}
                  {canViewGrossWeight && <Table.Summary.Cell index={3}><strong>{batch.totalGrossWeight?.toFixed(3)}g</strong></Table.Summary.Cell>}
                  {canViewPurchaseCost && <Table.Summary.Cell index={4}><strong>{fmt(batch.totalPurchaseCost)}</strong></Table.Summary.Cell>}
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>

        {['ITEMIZED', 'GROUPED'].includes(batch.batchType) && batch.items?.length > 0 && (
          <Col xs={24}>
            <Card title={`${batch.batchType === 'GROUPED' ? 'Grouped' : 'Itemized'} Items (${batch.items.length})`} size="small">
              <Table
                dataSource={batch.items}
                rowKey={(_, i) => i}
                size="small"
                pagination={false}
                scroll={{ x: 700 }}
                expandable={batch.batchType === 'GROUPED' ? {
                  rowExpandable: (r) => r.members?.length > 0,
                  expandedRowRender: (r) => (
                    <div style={{ fontSize: 12 }}>
                      <strong>Melted from:</strong>
                      <ul style={{ margin: '4px 0 0 16px' }}>
                        {r.members.map((m, i) => (
                          <li key={i}>{m.label} — {(m.grossWeight || 0).toFixed(3)}g @ {(m.purityPercent || 0).toFixed(2)}%{canViewPurchaseCost ? ` — ${fmt(m.priceAmount)}` : ''}</li>
                        ))}
                      </ul>
                    </div>
                  ),
                } : undefined}
                columns={[
                  { title: 'Label', dataIndex: 'label', key: 'label' },
                  ...(batch.batchType === 'GROUPED' ? [{
                    title: 'Type', key: 'type', width: 130,
                    render: (_, r) => (r.isMerged
                      ? <Tag color="volcano">Merged ×{r.members?.length || 0}</Tag>
                      : <Tag>Standalone</Tag>),
                  }, {
                    title: 'Melting Loss (g)', dataIndex: 'meltingLoss', key: 'meltingLoss', width: 100,
                    render: (v) => (v ? v.toFixed(3) : '—'),
                  }] : []),
                  { title: 'Gross Wt (g)', dataIndex: 'grossWeight', key: 'grossWeight', render: (v) => v?.toFixed(3) },
                  { title: 'Purity %', dataIndex: 'purityPercent', key: 'purityPercent', render: (v) => v?.toFixed(2) },
                  { title: 'Net Wt (g)', dataIndex: 'netWeight', key: 'netWeight', render: (v) => v?.toFixed(4) },
                  canViewPurchaseCost && { title: 'Price (₹)', dataIndex: 'priceAmount', key: 'priceAmount', render: (v) => fmt(v) },
                ].filter(Boolean)}
                summary={(data) => {
                  if (!canViewPurchaseCost) return null;
                  const totalPrice = data.reduce((s, i) => s + (i.priceAmount || 0), 0);
                  // Label(+Type+MeltLoss for GROUPED) + Gross + Purity + Net columns precede Price.
                  const leadSpan = batch.batchType === 'GROUPED' ? 6 : 4;
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={leadSpan}><strong>Totals</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={leadSpan}><strong>{fmt(totalPrice)}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}
